import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";

/**
 * Cookie-based auth context.
 *
 * Tokens live exclusively in httpOnly cookies set by the backend.
 * We probe `/auth/me` on mount — if the cookie is valid, we get back the
 * user record; if not, we treat the user as unauthenticated.
 *
 * No tokens are stored in localStorage anymore. The legacy `hrm_token`
 * key is cleared on logout to flush any pre-migration values.
 */
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null = checking, false = unauth, obj = user
    const [checking, setChecking] = useState(true);

    const checkSession = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch (e) {
            setUser(false);
        } finally {
            setChecking(false);
        }
    }, []);

    useEffect(() => {
        checkSession();
    }, [checkSession]);

    const login = async (companyId, email, password, employeeId = "") => {
        try {
            const payload = { email, password };
            if (employeeId) payload.employee_id = employeeId;
            else payload.company_id = companyId;
            const { data } = await api.post("/auth/login", payload);
            // Drop any pre-migration localStorage token to avoid double-auth confusion.
            localStorage.removeItem("hrm_token");
            setUser(data);
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
        }
    };

    const superAdminLogin = async (email, password) => {
        try {
            const { data } = await api.post("/auth/super-admin/login", { email, password });
            localStorage.removeItem("hrm_token");
            setUser(data);
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
        }
    };

    const registerCompany = async ({ companyId, companyName, address, adminName, adminEmail, adminPassword }) => {
        try {
            const { data } = await api.post("/companies/register", {
                company_id: companyId,
                company_name: companyName,
                address: address || "",
                admin_name: adminName,
                admin_email: adminEmail,
                admin_password: adminPassword,
            });
            return { ok: true, data };
        } catch (e) {
            return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
        }
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch (_) {
            // ignore
        }
        // Flush any legacy/impersonation flags.
        localStorage.removeItem("hrm_token");
        localStorage.removeItem("hrm_impersonation");
        setUser(false);
    };

    return (
        <AuthContext.Provider value={{ user, checking, login, superAdminLogin, logout, registerCompany, refresh: checkSession }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
}
